<template>
  <!--
    One native <select> per form type → an Open Dental imaging category (DefNum) on ONE site.
    Used by SuperAdmin → Features → Forms and by Bulk Form Copy. Blank = red + "Required": the
    backend refuses to seed/copy without every needed type mapped (see src/utils/formImagingMap.js).
    OTHER lists consent-like categories first and is never preselected.
  -->
  <div class="fim">
    <div v-for="type in rowTypes" :key="type" class="fim-row">
      <label class="fim-label" :for="idPrefix + '-' + type">{{ labels[type] }}</label>
      <div class="fim-control">
        <select
          :id="idPrefix + '-' + type"
          :data-test="idPrefix + '-' + type"
          class="form-control form-control-sm"
          :class="{ 'fim-select--required': isBlank(type) }"
          :disabled="disabled"
          @change="onChange(type, $event.target.value)"
        >
          <option value="" :selected="isBlank(type)">Select a category…</option>
          <template v-if="type === 'OTHER' && consentSplit.first.length">
            <option
              v-for="cat in consentSplit.first"
              :key="'first-' + cat.DefNum"
              :value="String(cat.DefNum)"
              :selected="isPicked(type, cat)"
            >{{ cat.ItemName }}</option>
            <option v-if="consentSplit.rest.length" disabled>──────</option>
            <option
              v-for="cat in consentSplit.rest"
              :key="'rest-' + cat.DefNum"
              :value="String(cat.DefNum)"
              :selected="isPicked(type, cat)"
            >{{ cat.ItemName }}</option>
          </template>
          <template v-else>
            <option
              v-for="cat in categories"
              :key="'cat-' + cat.DefNum"
              :value="String(cat.DefNum)"
              :selected="isPicked(type, cat)"
            >{{ cat.ItemName }}</option>
          </template>
        </select>
        <small v-if="isBlank(type)" class="fim-required-text">Required</small>
      </div>
    </div>
  </div>
</template>

<script>
import {
  FORM_IMAGING_TYPES,
  FORM_IMAGING_TYPE_LABELS,
  emptyCategoryMap,
  isBlankValue,
  splitConsentFirst,
} from '@/utils/formImagingMap';

export default {
  name: 'FormImagingCategoryMap',
  props: {
    // { OTHER, NP_FORM, MED_HX_UPDATE } → DefNum string ('' = not picked). Use with v-model.
    value: { type: Object, default: () => emptyCategoryMap() },
    // Which rows to show (FORM_IMAGING_TYPES order is kept).
    types: { type: Array, default: () => FORM_IMAGING_TYPES.slice() },
    categories: { type: Array, default: () => [] },
    consentCandidates: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    idPrefix: { type: String, default: 'form-imaging' },
  },
  data() {
    return { labels: FORM_IMAGING_TYPE_LABELS };
  },
  computed: {
    rowTypes() {
      return FORM_IMAGING_TYPES.filter(t => (this.types || []).indexOf(t) > -1);
    },
    consentSplit() {
      return splitConsentFirst(this.categories, this.consentCandidates);
    },
  },
  methods: {
    isBlank(type) {
      return isBlankValue((this.value || {})[type]);
    },
    isPicked(type, cat) {
      const v = (this.value || {})[type];
      return !isBlankValue(v) && String(v).trim() === String(cat.DefNum);
    },
    onChange(type, defNum) {
      this.$emit('input', { ...emptyCategoryMap(), ...(this.value || {}), [type]: defNum });
    },
  },
};
</script>

<style lang="scss" scoped>
.fim-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 4px 10px;
  padding: 3px 0;
}
.fim-label {
  flex: 0 0 240px;
  max-width: 100%;
  margin: 5px 0 0;
  font-size: 12px;
  color: #2c3e50;
}
.fim-control {
  flex: 1 1 200px;
  min-width: 0;
  max-width: 360px;
}
.fim-select--required {
  border-color: #dc3545;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.25);
}
.fim-required-text {
  display: block;
  color: #dc3545;
  font-size: 11px;
  font-weight: 600;
}
</style>
